import { useTheme } from "next-themes";
import { Toaster as Sonner, toast } from "sonner";

type ToasterProps = React.ComponentProps<typeof Sonner>;

// ── Global socket error suppression ──────────────────────────────────
// Monkey-patch the original sonner toast methods IN PLACE so that
// ALL consumers (even those importing directly from 'sonner') are covered.
const SOCKET_RE = /socket/i;

function isSuppressed(msg: unknown): boolean {
  return typeof msg === 'string' && SOCKET_RE.test(msg);
}

// Wrap a toast method so it silently drops socket-related messages
function patchMethod<T extends (...args: any[]) => any>(original: T, label: string): T {
  return ((...args: any[]) => {
    if (isSuppressed(args[0])) {
      console.warn(`[TOAST-SUPPRESS] ${label}("${args[0]}") - blocked`);
      return;
    }
    return original(...args);
  }) as T;
}

// Apply patches to the ORIGINAL toast object from sonner (mutates in place)
// This ensures even `import { toast } from 'sonner'` gets filtered.
const _patched = Symbol.for('__toast_patched__');
if (!(toast as any)[_patched]) {
  const orig = toast as any;

  // Wrap the default callable
  const originalCall = orig.bind ? orig.bind(orig) : orig;
  // We can't replace the function itself, but we can patch the named methods
  
  // Patch all string-accepting methods
  const methodsToPatch = ['error', 'warning', 'info', 'message', 'success', 'loading'] as const;
  for (const method of methodsToPatch) {
    if (typeof orig[method] === 'function') {
      orig[method] = patchMethod(orig[method], `toast.${method}`);
    }
  }
  
  (toast as any)[_patched] = true;
  console.log('[Sonner] Socket error suppression applied globally');
}

const Toaster = ({ ...props }: ToasterProps) => {
  const { theme = "system" } = useTheme();

  return (
    <Sonner
      theme={theme as ToasterProps["theme"]}
      className="toaster group"
      toastOptions={{
        classNames: {
          toast:
            "group toast group-[.toaster]:bg-background group-[.toaster]:text-foreground group-[.toaster]:border-border group-[.toaster]:shadow-lg",
          description: "group-[.toast]:text-muted-foreground",
          actionButton: "group-[.toast]:bg-primary group-[.toast]:text-primary-foreground",
          cancelButton: "group-[.toast]:bg-muted group-[.toast]:text-muted-foreground",
        },
      }}
      {...props}
    />
  );
};

export { Toaster, toast };
